/**
 * Tipografías del editor de plantillas: exactamente las mismas TTF que usa el PDF
 * (backend/fonts). Cada cara se descarga bajo demanda desde el backend (requiere
 * sesión; Avenir es licenciada y no se publica como archivo estático) y se registra
 * con la FontFace API usando el mismo nombre que en reportlab (p.ej. "AvenirNext-DemiBold").
 * Montserrat también se carga desde Google Fonts como respaldo.
 */
import api from "@/lib/api";

const GF_ID = "agro-tpl-montserrat";

// Espejo de FONT_FAMILIES en backend/template_render.py
const FAMILIES = {
  Montserrat: { bold: "Bold" },
  Avenir: { bold: "Heavy" },
  AvenirNext: { bold: "Bold" },
};
const HEAVY = new Set(["SemiBold", "DemiBold", "Bold", "ExtraBold", "Heavy", "Black"]);
const loaded = {};

export function ensureTemplateFonts() {
  if (typeof document === "undefined" || document.getElementById(GF_ID)) return;
  const link = document.createElement("link");
  link.id = GF_ID;
  link.rel = "stylesheet";
  link.href = "https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,400;0,500;0,600;0,700;0,900;1,400;1,700&display=swap";
  document.head.appendChild(link);
}

function loadFace(name) {
  if (loaded[name] || typeof document === "undefined" || !document.fonts || typeof FontFace === "undefined") return;
  loaded[name] = true;
  api.get(`/document-templates/font-files/${name}`, { responseType: "arraybuffer" })
    .then(({ data }) => new FontFace(name, data).load())
    .then((face) => document.fonts.add(face))
    .catch(() => { loaded[name] = false; });
}

/** Misma regla que resolve_font() del backend: nombre de la cara TTF a usar. */
export function resolveFace(value, bold, italic) {
  let v = (value || "").trim().replace("Avenir Next", "AvenirNext");
  let [fam, w] = v.includes("-") ? [v.slice(0, v.indexOf("-")), v.slice(v.indexOf("-") + 1)] : [v, "Regular"];
  if (!FAMILIES[fam]) return null;
  let it = !!italic;
  if (w.endsWith("Italic")) { it = true; w = w.slice(0, -6) || "Regular"; }
  if (!w) w = "Regular";
  if (bold && !HEAVY.has(w)) w = FAMILIES[fam].bold;
  if (w === "Regular") return it ? `${fam}-Italic` : `${fam}-Regular`;
  return `${fam}-${w}${it ? "Italic" : ""}`;
}

export const DEFAULT_FONTS = [
  { value: "Helvetica", label: "Helvetica", available: true },
  { value: "Times-Roman", label: "Times", available: true },
  { value: "Courier", label: "Courier", available: true },
  { value: "Montserrat", label: "Montserrat", available: true },
  { value: "Montserrat-Medium", label: "Montserrat Medium", available: true },
  { value: "Montserrat-SemiBold", label: "Montserrat SemiBold", available: true },
  { value: "Montserrat-Black", label: "Montserrat Black", available: true },
  { value: "Avenir", label: "Avenir", available: true },
  { value: "AvenirNext", label: "Avenir Next", available: true },
];

/** Estilo CSS equivalente a la fuente del PDF para un elemento. */
export function fontCss(el) {
  const f = el.font || "Helvetica";
  const face = resolveFace(f, el.bold, el.italic);
  if (face) {
    loadFace(face);
    return { fontFamily: `'${face}', 'Montserrat', sans-serif`, fontWeight: "normal", fontStyle: "normal" };
  }
  let family = "Helvetica, Arial, sans-serif";
  if (f.startsWith("Times")) family = "'Times New Roman', Times, serif";
  else if (f.startsWith("Courier")) family = "'Courier New', Courier, monospace";
  return {
    fontFamily: family,
    fontWeight: el.bold ? 700 : 400,
    fontStyle: el.italic ? "italic" : "normal",
  };
}

/** Tamaños de papel en px @96dpi. */
export const PAPER = { letter: [816, 1056], a4: [794, 1123], legal: [816, 1344] };
